import { Jute } from ".";

export const enum ITranslationKey {
    launch = 'launch',
    reset = 'reset',
    place = 'place',
    surfaceFound = 'surfaceFound',
    findingSurface = 'findingSurface',
    pointFaceToCam = 'pointFaceToCam',
    pointCamToTargetImg = 'pointCamToTargetImg',
    close = 'close',
    pinToScreenOn = 'pinToScreenOn',
    pinToScreenOff = 'pinToScreenOff',
}

export type ILocale = 'en' | 'de' | 'es' | 'fr' | 'pt' | 'bg' | 'ru';
type ILocaleMessages = Record<keyof typeof ITranslationKey, string>;
type TranslationsMap = Record<ILocale, ILocaleMessages>;

export const translationMap: TranslationsMap = {
    en: {
        [ITranslationKey.launch]: 'Launch',
        [ITranslationKey.reset]: 'Reset',
        [ITranslationKey.place]: 'Place',
        [ITranslationKey.surfaceFound]: 'Surface found – Tap to place',
        [ITranslationKey.findingSurface]: 'Finding surface...',
        [ITranslationKey.pointFaceToCam]: 'Point camera at face',
        [ITranslationKey.pointCamToTargetImg]: 'Point camera at target image',
        [ITranslationKey.close]: 'Close',
        [ITranslationKey.pinToScreenOn]: 'Pin to screen: On',
        [ITranslationKey.pinToScreenOff]: 'Pin to screen: Off',
    },
    de: {
        [ITranslationKey.launch]: 'Starten',
        [ITranslationKey.reset]: 'Neu platzieren',
        [ITranslationKey.place]: 'Platzieren',
        [ITranslationKey.surfaceFound]: 'Tippe zum Platzieren',
        [ITranslationKey.findingSurface]: 'Suche Oberfläche...',
        [ITranslationKey.pointFaceToCam]: 'Kamera auf Gesicht richten',
        [ITranslationKey.pointCamToTargetImg]: 'Kamera auf Bild richten',
        [ITranslationKey.close]: 'Schließen',
        [ITranslationKey.pinToScreenOn]: 'Am Bildschirm fixiert: An',
        [ITranslationKey.pinToScreenOff]: 'Am Bildschirm fixiert: Aus',
    },
    es: {
        [ITranslationKey.launch]: 'Iniciar',
        [ITranslationKey.reset]: 'Reiniciar',
        [ITranslationKey.place]: 'Colocar',
        [ITranslationKey.surfaceFound]: 'Toque para colocar',
        [ITranslationKey.findingSurface]: 'Buscando superficie...',
        [ITranslationKey.pointFaceToCam]: 'Apunta la cámara a la cara',
        [ITranslationKey.pointCamToTargetImg]: 'Apunta la cámara a la imagen',
        [ITranslationKey.close]: 'Cerrar',
        [ITranslationKey.pinToScreenOn]: 'Fijar en pantalla: Activado',
        [ITranslationKey.pinToScreenOff]: 'Fijar en pantalla: Desactivado',
    },
    fr: {
        [ITranslationKey.launch]: 'Lancer',
        [ITranslationKey.reset]: 'Repositionner',
        [ITranslationKey.place]: 'Placer',
        [ITranslationKey.surfaceFound]: 'Touchez pour placer',
        [ITranslationKey.findingSurface]: 'Recherche d’une surface...',
        [ITranslationKey.pointFaceToCam]: 'Pointez la caméra vers le visage',
        [ITranslationKey.pointCamToTargetImg]: 'Pointez la caméra vers l’image',
        [ITranslationKey.close]: 'Fermer',
        [ITranslationKey.pinToScreenOn]: 'Épingler à l’écran : Activé',
        [ITranslationKey.pinToScreenOff]: 'Épingler à l’écran : Désactivé',
    },
    pt: {
        [ITranslationKey.launch]: 'Iniciar',
        [ITranslationKey.reset]: 'Recolocar',
        [ITranslationKey.place]: 'Colocar',
        [ITranslationKey.surfaceFound]: 'Toque para colocar',
        [ITranslationKey.findingSurface]: 'Procurando superfície...',
        [ITranslationKey.pointFaceToCam]: 'Aponte a câmera para o rosto',
        [ITranslationKey.pointCamToTargetImg]: 'Aponte a câmera para a imagem',
        [ITranslationKey.close]: 'Fechar',
        [ITranslationKey.pinToScreenOn]: 'Fixar no ecrã: Activado',
        [ITranslationKey.pinToScreenOff]: 'Fixar no ecrã: Desactivado',
    },
    bg: {
        [ITranslationKey.launch]: 'Започни',
        [ITranslationKey.reset]: 'Започнете отначало',
        [ITranslationKey.place]: 'Постави',
        [ITranslationKey.surfaceFound]: 'Натисни за да поставиш',
        [ITranslationKey.findingSurface]: 'Търсене на повърхност...',
        [ITranslationKey.pointFaceToCam]: 'Насочете камерата към лицето',
        [ITranslationKey.pointCamToTargetImg]: 'Насочете камерата към изубражението',
        [ITranslationKey.close]: 'Затвори',
        [ITranslationKey.pinToScreenOn]: 'Закрепете за екрана: Включено',
        [ITranslationKey.pinToScreenOff]: 'Закрепете за екрана: Изключено',
    },
    ru: {
        [ITranslationKey.launch]: 'Запустить',
        [ITranslationKey.reset]: 'Сбросить',
        [ITranslationKey.place]: 'Разместить',
        [ITranslationKey.surfaceFound]: 'Нажмите, чтобы разместить',
        [ITranslationKey.findingSurface]: 'Поиск поверхности...',
        [ITranslationKey.pointFaceToCam]: 'Направьте камеру на лицо',
        [ITranslationKey.pointCamToTargetImg]: 'Направьте камеру на изображение',
        [ITranslationKey.close]: 'Закрыть',
        [ITranslationKey.pinToScreenOn]: 'Закрепить на экране: Вкл',
        [ITranslationKey.pinToScreenOff]: 'Закрепить на экране: Выкл',
    },
} as const;

export const getTranslation = (message: ITranslationKey, capitalize?: boolean) => {
    const baseLocale = navigator.language.split('-')[0] as ILocale;
    let locale = (baseLocale in translationMap) ? baseLocale : 'en';
    if (Jute.forcedLocale) locale = Jute.forcedLocale;

    let text = translationMap[locale][message];

    // Note: Manual capitalization in the map ensures the first letter is always upper case.
    // This logic remains for dynamic cases or specific overrides.
    if (capitalize) {
        text = text.charAt(0).toLocaleUpperCase(baseLocale in translationMap ? navigator.language : 'en-US') + text.slice(1);
    }

    return text;
};